// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the dataset's legend in the headset (the AR refresh,
 * 2026-10-04): a slim panel beside the globe's left side with the colour
 * bar, its values and units — or a classified release's classes — drawn
 * from the same scale and display the browser's floating legend uses, so
 * the headset's colours finally say what they mean. A picture dataset
 * has none; the panel hides.
 *
 * AYNI (owner, 2026-10-11): its ✕ parks it on the side panel's View page
 * (vrPanels `drawParkedLegend`), flying there (vrSession); the View
 * page puts it back.
 *
 * `buildVrLegend` is the pure description (tested); the panel only
 * paints it.
 *
 * AYNI (DSA 1.8): a vector layer that supersedes the dataset's video or
 * legend picture brings its own legend (`vrLegendFromVector`): its colour
 * scale or categories, and for points a size key under them.
 */

import type * as THREE from 'three'
import { formatNumber } from '../i18n/format'
import type { ColorScale } from '../types/color-scale'
import { releaseOf } from '../types/color-scale'
import {
  DEFAULT_DISPLAY,
  buildDisplayLut,
  colorbarTicks,
  displayGradientStops,
  positionOfValue,
  valueAtPosition,
  type ColorScaleDisplay,
} from './colorScaleDisplay'
import { referenceShort, type ReferenceLine } from './referenceLines'
import { createCanvasPanel, PANEL_COLORS } from './vrPanelKit'
import type { VectorLegendModel } from './vector/vectorLegend'

export interface VrLegend {
  title: string
  units: string | null
  /** A colour bar, or a classified release's classes. */
  kind: 'bar' | 'classes'
  /** CSS colours along the bar, low to high. */
  stops: string[]
  /** Labelled values along the bar, 0 = low end. */
  ticks: Array<{ position: number; label: string }>
  classes: Array<{ color: string; label: string }>
  /** DSA 1.8 points: circles at the legend's values, diameters relative to the largest. */
  sizes?: { label: string; items: Array<{ label: string; relative: number }> } | null
  /** AYNI (owner, 2026-10-14): reference lines on the bar, 0 = low end, each with its short name. */
  refs?: Array<{ position: number; label: string }>
}

/** AYNI: how close to a reference line's name (a fraction of the bar) a tick or another name may sit. */
const REF_CLEARANCE = 0.08

/** A vector layer's legend (vectorLegend.ts) for the headset's panel. */
export function vrLegendFromVector(model: VectorLegendModel): VrLegend {
  const c = model.color
  return {
    title: model.title,
    units: c?.label || (c?.kind === 'bar' ? c.units : '') || null,
    kind: c?.kind === 'classes' ? 'classes' : 'bar',
    stops: c?.kind === 'bar' ? c.samples : [],
    ticks: c?.kind === 'bar' ? c.ticks : [],
    classes: c?.kind === 'classes' ? c.classes : [],
    sizes: model.size,
  }
}

function rgba(c: readonly [number, number, number, number]): string {
  return `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${(c[3] / 255).toFixed(3)})`
}

/** The legend of a scale under a display, as the browser's colour bar draws it. */
export function buildVrLegend(scale: ColorScale, display: ColorScaleDisplay, title: string, references: readonly ReferenceLine[] = []): VrLegend {
  const release = releaseOf(scale)
  if (release?.kind === 'luma8-classified' && release.classes.length > 0) {
    const lut = buildDisplayLut(scale, DEFAULT_DISPLAY)
    return {
      title,
      units: null,
      kind: 'classes',
      stops: [],
      ticks: [],
      classes: release.classes.map((c) => {
        const o = Math.max(0, Math.min(255, Math.round(c.code))) * 4
        return { color: `rgb(${lut[o]}, ${lut[o + 1]}, ${lut[o + 2]})`, label: c.label }
      }),
    }
  }
  // AYNI: the reference lines within the bar's range.
  const lo = valueAtPosition(scale, display, 0)
  const hi = valueAtPosition(scale, display, 1)
  const refs = references
    .filter((line) => line.value >= Math.min(lo, hi) && line.value <= Math.max(lo, hi))
    .map((line) => ({ position: positionOfValue(scale, display, line.value), label: referenceShort(line) }))
  return {
    title,
    units: scale.units ?? null,
    kind: 'bar',
    stops: displayGradientStops(scale, display, 24).map((s) => rgba(s.rgba)),
    ticks: colorbarTicks(scale, display, 5).map((tick) => ({
      position: tick.position,
      label: formatNumber(tick.value, { maximumSignificantDigits: 3 }),
    })),
    classes: [],
    ...(refs.length ? { refs } : {}),
  }
}

/** World size: a tall slim panel. */
const PANEL_W = 0.2
const PANEL_H = 0.4
const CANVAS_W = 400
const CANVAS_H = 800

/** A press on the legend panel: its ✕. */
export type VrLegendAction = 'park'

export interface VrLegendHandle {
  readonly mesh: THREE.Mesh
  /** Show a legend, or hide the panel with null. Redraws only on change. */
  set(legend: VrLegend | null): void
  isVisible(): boolean
  hitTest(uv: { x: number; y: number }): VrLegendAction | null
  dispose(): void
}

/** The ✕ in the panel's top corner, in canvas pixels. */
const CLOSE_SIZE = 64
const CLOSE_INSET = 22

export function createVrLegend(THREE_: typeof THREE): VrLegendHandle {
  const panel = createCanvasPanel<VrLegendAction>(THREE_, {
    widthM: PANEL_W,
    heightM: PANEL_H,
    canvasW: CANVAS_W,
    canvasH: CANVAS_H,
  })
  panel.mesh.visible = false
  let drawn = ''

  return {
    mesh: panel.mesh,
    set(legend) {
      const key = legend ? JSON.stringify(legend) : ''
      if (key === drawn) return
      drawn = key
      panel.mesh.visible = legend !== null
      if (!legend) return
      panel.draw((p) => {
        p.frame(28)
        const pad = 30
        p.button(CANVAS_W - CLOSE_INSET - CLOSE_SIZE, CLOSE_INSET, CLOSE_SIZE, CLOSE_SIZE, '', 'park', { icon: 'close', radius: CLOSE_SIZE / 2 })
        // The title keeps clear of the ✕.
        const titleLines = p.wrap(legend.title, CANVAS_W - pad * 2 - CLOSE_SIZE - 6, 2, 28, 700)
        titleLines.forEach((line, i) => p.text(line, pad, 50 + i * 34, { size: 28, weight: 700 }))
        let top = 50 + titleLines.length * 34 + 6
        if (legend.units) {
          p.text(legend.units, pad, top + 4, { size: 24, color: PANEL_COLORS.textSecondary, maxWidth: CANVAS_W - pad * 2 })
          top += 40
        }
        top += 16
        // DSA 1.8: the size key takes the panel's foot, under the bar or the classes.
        const sizes = legend.sizes && legend.sizes.items.length ? legend.sizes : null
        const sizesH = sizes ? 190 : 0
        if (sizes) {
          const y0 = CANVAS_H - sizesH
          p.text(sizes.label, pad, y0 + 18, { size: 24, color: PANEL_COLORS.textSecondary, maxWidth: CANVAS_W - pad * 2 })
          const cellW = (CANVAS_W - pad * 2) / sizes.items.length
          const maxD = Math.min(84, cellW - 12)
          const baseY = y0 + 40 + maxD
          sizes.items.forEach((item, i) => {
            const d = Math.max(8, maxD * item.relative)
            const cx = pad + cellW * (i + 0.5)
            p.g.beginPath()
            p.g.arc(cx, baseY - d / 2, d / 2, 0, Math.PI * 2)
            p.g.fillStyle = 'rgba(255, 255, 255, 0.16)'
            p.g.fill()
            p.g.strokeStyle = PANEL_COLORS.text
            p.g.lineWidth = 2
            p.g.stroke()
            const w = p.g.measureText(item.label).width
            p.text(item.label, cx - Math.min(w, cellW) / 2, baseY + 24, { size: 26, weight: 600, maxWidth: cellW })
          })
        }
        if (legend.kind === 'classes') {
          const rowH = Math.min(64, (CANVAS_H - top - 30 - sizesH) / Math.max(1, legend.classes.length))
          legend.classes.forEach((c, i) => {
            const y = top + i * rowH
            p.box(pad, y + 6, 40, rowH - 16, 8, c.color)
            p.text(c.label, pad + 56, y + rowH / 2, { size: Math.min(26, rowH * 0.5), maxWidth: CANVAS_W - pad * 2 - 56 })
          })
          return
        }
        // The bar runs bottom (low) to top (high), ticks to its right.
        const barX = pad
        const barW = 56
        const barTop = top
        const barBottom = CANVAS_H - 40 - sizesH
        const grad = p.g.createLinearGradient(0, barBottom, 0, barTop)
        legend.stops.forEach((c, i) => grad.addColorStop(i / Math.max(1, legend.stops.length - 1), c))
        p.roundPath(barX, barTop, barW, barBottom - barTop, 10)
        p.g.fillStyle = grad
        p.g.fill()
        p.g.strokeStyle = PANEL_COLORS.border
        p.g.lineWidth = 2
        p.g.stroke()
        // AYNI: reference lines dashed across the bar; a name for each not crowded by an earlier one, in place of the ticks beside it.
        const named: number[] = []
        for (const ref of legend.refs ?? []) {
          const y = barBottom - ref.position * (barBottom - barTop)
          p.g.save()
          p.g.setLineDash([10, 7])
          p.g.lineWidth = 4
          p.g.strokeStyle = 'rgba(0, 0, 0, 0.85)'
          p.g.beginPath()
          p.g.moveTo(barX - 6, y)
          p.g.lineTo(barX + barW + 14, y)
          p.g.stroke()
          p.g.lineWidth = 2
          p.g.strokeStyle = '#fff'
          p.g.stroke()
          p.g.restore()
          if (named.some((q) => Math.abs(q - ref.position) < REF_CLEARANCE)) continue
          named.push(ref.position)
          p.text(ref.label, barX + barW + 24, y, { size: 30, weight: 800, maxWidth: CANVAS_W - barW - pad * 2 - 30 })
        }
        for (const tick of legend.ticks) {
          if (named.some((q) => Math.abs(q - tick.position) < REF_CLEARANCE)) continue
          const y = barBottom - tick.position * (barBottom - barTop)
          p.g.strokeStyle = PANEL_COLORS.text
          p.g.lineWidth = 3
          p.g.beginPath()
          p.g.moveTo(barX + barW, y)
          p.g.lineTo(barX + barW + 14, y)
          p.g.stroke()
          p.text(tick.label, barX + barW + 24, y, { size: 30, weight: 600, maxWidth: CANVAS_W - barW - pad * 2 - 30 })
        }
      })
    },
    isVisible: () => panel.mesh.visible,
    hitTest: (uv) => (panel.mesh.visible ? panel.hitTest(uv) : null),
    dispose() {
      panel.dispose()
    },
  }
}
